import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Avatar,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// Icons
import HistoryIcon from '@mui/icons-material/History';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const AboutPage = () => {
  const { t } = useTranslation();

  const teamMembers = [
    {
      name: 'Doron Berger',
      position: 'CEO & Technical Director',
      avatar: require('../assets/team/doron1.png'),
    },
    {
      name: 'Jonathan Berkovits',
      position: 'Principal Software Engineer',
      avatar: require('../assets/team/jonathan1.png'),
    },
    {
      name: 'Saar Koren',
      position: 'Principal Backend Engineer',
      avatar: require('../assets/team/saar1.png'),
    },
    {
      name: 'Assaf Shalev',
      position: 'Marketing Director',
      avatar: require('../assets/team/assaf1.png'),
    }
  ];

  const values = [
    {
      title: t('about.innovation'),
      description: t('about.innovationDesc')
    },
    {
      title: t('about.quality'),
      description: t('about.qualityDesc')
    },
    {
      title: t('about.customerFocus'),
      description: t('about.customerFocusDesc')
    },
    {
      title: t('about.integrity'),
      description: t('about.integrityDesc')
    }
  ];

  const MotionBox = motion(Box);

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', pt: 8, pb: 12 }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ mb: 12 }}>
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            component="h1"
            variant="h2"
            align="center"
            sx={{ fontWeight: 800, mb: 3, color: 'var(--text-primary)' }}
          >
            {t('about.title')}
          </Typography>

          <Typography
            variant="h5"
            align="center"
            sx={{
              mb: 2,
              color: 'var(--text-secondary)',
              maxWidth: '700px',
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            {t('about.subtitle')}
          </Typography>
        </motion.div>
      </Container>

      {/* Company History */}
      <Container maxWidth="lg" sx={{ mb: 12 }}>
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <Box
            sx={{
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-md)',
              p: { xs: 3, md: 6 },
            }}
          >
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <HistoryIcon sx={{ fontSize: 48, color: 'var(--color-blue)' }} />
                <Typography variant="h3" sx={{ fontWeight: 800, color: 'var(--text-primary)' }}>
                  {t('about.history')}
                </Typography>
              </Box>

              <Box sx={{ borderTop: '1px solid var(--border-color)', pt: 4 }}>
                <Typography sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, mb: 2, fontSize: '1.05rem' }}>
                  {t('about.historyP1')}
                </Typography>
                <Typography sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, mb: 2, fontSize: '1.05rem' }}>
                  {t('about.historyP2')}
                </Typography>
                <Typography sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: '1.05rem' }}>
                  {t('about.historyP3')}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </MotionBox>
      </Container>

      {/* Our Team */}
      <Container maxWidth="lg" sx={{ mb: 12 }}>
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <Stack spacing={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <GroupsIcon sx={{ fontSize: 48, color: 'var(--color-blue)' }} />
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'var(--text-primary)' }}>
                {t('about.team')}
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {teamMembers.map((member, index) => (
                <Grid
                  item
                  key={member.name}
                  xs={12}
                  sm={6}
                  md={3}
                  component={motion.div}
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1, ease: 'easeOut' }}
                >
                  <motion.div
                    whileHover={{ y: -8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 3,
                        background: 'var(--card-bg)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-lg)',
                        boxShadow: 'var(--shadow-sm)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: 'var(--shadow-lg)',
                        },
                      }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Avatar
                          alt={member.name}
                          src={member.avatar}
                          sx={{
                            width: 140,
                            height: 140,
                            mx: 'auto',
                            mb: 2,
                            border: '3px solid var(--color-blue-light)',
                          }}
                        />
                      </motion.div>
                      <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 1 }}>
                        {member.name}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                        {member.position}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </MotionBox>
      </Container>

      {/* Our Values */}
      <Container maxWidth="lg">
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <Stack spacing={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <EmojiObjectsIcon sx={{ fontSize: 48, color: 'var(--color-gold)' }} />
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'var(--text-primary)' }}>
                {t('about.values')}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {values.map((value, index) => (
                <Grid
                  item
                  key={value.title}
                  xs={12}
                  sm={6}
                  component={motion.div}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1, ease: 'easeOut' }}
                >
                  <motion.div
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      sx={{
                        p: 4,
                        background: 'var(--card-bg)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-lg)',
                        boxShadow: 'var(--shadow-sm)',
                        display: 'flex',
                        gap: 3,
                        h: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: 'var(--shadow-md)',
                        },
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 32, color: 'var(--color-blue)', flexShrink: 0 }} />
                      <Box>
                        <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 1 }}>
                          {value.title}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                          {value.description}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default AboutPage;
